
<script setup>
    import { computed, reactive, onMounted, watch } from 'vue'
    import { ref as databaseRef, onValue, update, push, child, set } from "firebase/database";
    import { useRoute } from 'vue-router'
    import HeaderComponent from "../../components/common/HeaderComponent.vue"
    import AdminBar from '../../components/administrator/AdminBar.vue'
    import DefaultModalComponent from "../../components/common/DefaultModalComponent.vue"
    import Firebase from "../../firebase_settings/index.js"
    import utilFunctions from "../../firebase_settings/utils.js"
    import Error from "../../firebase_settings/error.js"

    const database = Firebase.database;
    const route = useRoute()

    //データアップデート等のリアクティブオブジェクト
    const reactiveObj = reactive({
        //再描画キー
        renderKey: 0,
        //実行種別
        execType: '',
        //特定もしくは全体のパス
        execCorporate: '',
        //更新対象のルートパス
        rootPath: '',
        //更新対象のルートパス(表示用)
        displayRootPath: '',
        //更新もしくは削除時の更新するjsonデータ(文字列)
        jsonData: '',
    })
    //企業コードのリアクティブオブジェクト
    const reactiveInput = reactive({
        //企業コード（特定を選択時に使用）
        CorporateId: '',
    })
    //モーダルのリアクティブオブジェクト
    const reactiveModalObj = reactive({
        modalFlag :false,
        modalMessage: '',
        modalRenderKey: 0
    })
    //各種選択した項目でそれぞれ値を振り分ける
    //exec:update(対応済)、delete(未対応)
    const execChoice = (exec, key) => {
        reactiveObj[key] = exec
        switch(exec) {
            //全企業対応
            case 'all':
                reactiveInput.CorporateId = '*'
                break
            //個別企業対応
            case 'specific':
                reactiveInput.CorporateId = reactiveInput.CorporateId && reactiveInput.CorporateId != '*' ? reactiveInput.CorporateId : ''
                break
            //特定の企業の場合
            case 'CorporateId':
                reactiveObj.displayRootPath = `${reactiveObj.rootPath}/${reactiveInput.CorporateId}/`
                break
            case 'LoginUser':
            case 'root':
                break
        }
    }
    const dataUpload = (event) => {
        try {
            const file = event.target.files[0]
            //対応拡張子を.jpeg .jpg .pngにバリデーション
            const regexp = new RegExp(/.*\.json/, 'i')
            //拡張子チェッカー
            if(regexp.test(file['name'])) {
                const reader = new FileReader();
                const loadFunc = () => {
                    reactiveObj.jsonData = reader.result
                }
                reader.onload = loadFunc;
                //文字化け防止
                reader.readAsText(file)
            }
            else {
                alert(Error.errorResult({ code: 'DUE0001'}, route.name))
            }
        }
        catch {
            alert(Error.errorResult({ code: 'DUE0002'}, route.name))
            document.getElementById("fileLabel").innerHTML = 'jsonデータ'
            reactiveObj.jsonData = {}
            reactiveObj.renderKey++
        }
    }
    //モーダルウィンドウを開く際に実行
    const showDefaultModal = (message) => {
        if(
            reactiveObj.execType &&
            reactiveObj.rootPath &&
            (reactiveObj.execCorporate == 'all' || reactiveInput.CorporateId) &&
            reactiveObj.jsonData
        ) {
            //モーダルを表示
            reactiveModalObj.modalFlag = true
            //モーダル上の表示する文面
            reactiveModalObj.modalMessage = message
            //モーダル部分を再描画
            reactiveModalObj.modalRenderKey++
        }
        else {
            alert(Error.errorResult({ code: 'DUE0003'}, route.name))
        }
    }
    //入力したパスに対して、更新すべき対象のパスをすべて.区切りにして配列で返す
    const getLeafKeys = (obj, maxDepth, inputPathArray) => {
        let keys = [];
        let stack = [{ obj, depth: 1, parentKey: '' }];

        while (stack.length) {
            //stackの中身を取り出し値に格納
            const { obj, depth, parentKey } = stack.pop();
            //親のパスの長さが指定の深さと一致かつ親が存在している場合にはkeysに格納
            if(parentKey.split('.').length == maxDepth && parentKey) {
                keys.push(parentKey)
            }
            //objがオブジェクトでない（＝まだ子のパスが存在しない）もしくは存在しない、または深さが指定の深さを超えた場合には最初に戻る
            if (typeof obj !== 'object' || obj === null || depth > maxDepth) {
                continue;
            }

            //objに対して繰り返し
            for (let key in obj) {
                //現在の深さ-1のinputPathArrayパスの要素に対してアスタリスクでない場合にはkeyを更新
                if(inputPathArray[depth-1] != '*') {
                    key = inputPathArray[depth-1]
                }
                //親のパスが存在するならkeyを連結する
                const fullKey = parentKey ? `${parentKey}.${key}` : key;
                //stackに格納し、深さを+1する
                stack.push({ obj: obj[key], depth: depth + 1, parentKey: fullKey });
                //現在の深さ-1のinputPathArrayパスの要素に対してアスタリスクでない場合には終了（要するにpushしたので次へ）
                if(inputPathArray[depth-1] != '*') {
                    break
                }
            }
        }
        return keys;
    }
    //targetPathArrayの数だけ、各キーに対応した値を更新する
    const getValueByPath = (obj, targetPathArray) => {
        targetPathArray.forEach(targetPath => {
            //.区切りのパスなのでそれらを分割
            const pathArray = targetPath.split('.')
            pathArray.reduce((accumulator, key, index) => {
                //末端に到達したら、そこの箇所のオブジェクトに対して入力したデータを追加
                if (index === pathArray.length - 1) {
                    Object.assign(accumulator[key], JSON.parse(reactiveObj.jsonData))
                }
                //途中のオブジェクトがなければ作成
                else {
                    accumulator[key] = accumulator[key] || {};
                }
                // 次の階層に進む
                return accumulator[key];
            }, obj);
        });
        return obj;
    };
    //DBの更新
    const updateData = async() => {
        //企業情報の更新の場合
        if(reactiveObj.rootPath == 'CorporateId') {
            //CorporateId/企業コード/XXのXX以降の部分を/区切りに配列で返す
            const inputPathArray = document.getElementById("inputPath").value.split('\/').filter(path => path)
            //更新対象の企業をリスト化
            const corporateIdList = reactiveInput.CorporateId == '*' ? await utilFunctions.getCorporateIdList() : [reactiveInput.CorporateId]
            //各企業に対して更新を行う
            for(const corporateId of corporateIdList) {
                const getDataRef = databaseRef(database, `${reactiveObj.rootPath}/${(corporateId)}`);
                const getData = await new Promise((resolve) => {
                    onValue(getDataRef, (snapshot) => {
                        resolve(snapshot.val());
                    }, {
                        onlyOnce: true
                    });
                });
                //深さの取得
                let maxDepth = inputPathArray.length
                //*の値を実際のパスに変換してリストで返す。ない場合にはリストの長さは1になる
                const targetPathArray = getLeafKeys(getData, maxDepth, inputPathArray)
                //更新対象のデータを取得
                let changeData = JSON.parse(reactiveObj.jsonData)
                //更新する後続のパス
                let childPath = '/'
                //深さが1以上の時はchangeDataをできる限り深く堀り、適切に更新する
                if(maxDepth) {
                    //更新対象のデータを取得（この時点では更新不要なキーも含まれている）
                    changeData = getValueByPath(getData, targetPathArray)
                    //inputPathArrayの長さだけ繰り返し
                    for(const inputPath of inputPathArray) {
                        //アスタリスクでない場合にはchangeDataの値とchildPathの値を更新
                        if(inputPath != '*') {
                            changeData = changeData[inputPath]
                            childPath += `${inputPath}/`
                        }
                        //アスタリスクの場合はそこで終了
                        else {
                            break
                        }
                    }
                }
                update(child(getDataRef, `${childPath}`), changeData)
            }
        }
        //管理者情報の更新の場合
        else if (reactiveObj.rootPath == 'LoginUser') {
            const getDataRef = databaseRef(database, `${reactiveObj.rootPath}/`);
            const getData = await new Promise((resolve) => {
                onValue(getDataRef, (snapshot) => {
                    resolve(snapshot.val());
                }, {
                    onlyOnce: true
                });
            });
            //各管理者に対して繰り返し
            for(const key of Object.keys(getData)) {
                //特定の企業もしくは、全体の場合に更新する
                if(reactiveInput.CorporateId == '*' || getData[key].CorporateId == reactiveInput.CorporateId) {
                    await update(child(getDataRef, `${key}`), JSON.parse(reactiveObj.jsonData));
                }
            }
        }
        //ルートパスの場合
        else if (reactiveObj.rootPath == 'root') {
            const getDataRef = databaseRef(database, `/`);
            //そのまま更新（コンソール側はページ更新しないと表示されない？）
            await update(getDataRef, JSON.parse(reactiveObj.jsonData));
        }
    }
    //現在未対応（そもそも必要ある？）
    const deleteData = () => {}
    //モーダルウィンドウを閉じる際に実行
    const closeDefaultModal = (editFlag) => {
        //モーダルウィンドウを閉じる
        reactiveModalObj.modalFlag= false
        //DBの変更有無
        if(editFlag) {
            //更新
            if(reactiveObj.execType == 'update') {
                updateData()
            }
            //削除
            else if(reactiveObj.execType == 'delete') {
                deleteData()
            }
        }
    }
    //企業コードの入力を監視
    watch(reactiveInput, () => {
        //個別の場合、入力した企業コードをパスに反映させる
        if(reactiveObj.execCorporate == 'specific' && reactiveObj.rootPath) {
            reactiveObj.displayRootPath = `${reactiveObj.rootPath}/${reactiveInput.CorporateId}/`
        }
        //全体の場合には後続処理のためにアスタリスクに固定する
        else if(reactiveObj.execCorporate == 'all' && reactiveObj.rootPath) {
            reactiveObj.displayRootPath = `${reactiveObj.rootPath}/*/`
        }
    })
</script>
<template>
    <!-- コメントアウト用 -->
    <HeaderComponent></HeaderComponent>
    <AdminBar></AdminBar>
    <main>
        <div id="loadingDisplay" class="loading" v-show="reactiveObj.loadingFlag">
            <div class="load-text load-blink">処理中...</div>
        </div>
        <div class="database" :key="`${reactiveObj.renderKey}`">
            <div class="titleArea">
                <p class="title">データアップデート</p>
            </div>
            <p class="cation">
                ※注意※<br>
                更新する際に、新規の存在しないパスを作成したい場合には、一度作成をしないと反映されません。<br>
                もしくは指定するパスを1つ上の階層までの入力にし、新規で作成するパスはjsonデータに入力して下さい。
            </p>
            <div class="editArea">
                <div class="inline-radio divPadding">
                    <div><input type="radio" name="execType" @change="execChoice('update', 'execType')"><label>更新</label></div>
                    <div><input type="radio" name="execType" @change="execChoice('delete', 'execType')" disabled><label>削除</label></div>
                </div>
                <div class="jsonData divPadding" v-show="reactiveObj.execType">
                    <div class="inputJson">
                        <label id="fileLabel">jsonデータ<input type="file" id="fileButton" @change="dataUpload"></label>
                    </div>
                    <div class="jsonDataContent">
                        <p>データの中身</p>
                        <textarea id="jsonData" v-model="reactiveObj.jsonData"></textarea>
                    </div>
                </div>
                <div class="inline-radio divPadding">
                    <div><input type="radio" name="execCorporate" @change="execChoice('specific', 'execCorporate')"><label>特定の企業</label></div>
                    <div><input type="radio" name="execCorporate" @change="execChoice('all', 'execCorporate')"><label>全体</label></div>
                </div>
                <div class="inputCorporate divPadding" v-show="reactiveObj.execCorporate == 'specific'">
                    <input type="text" id="inputCorporate" v-model="reactiveInput.CorporateId" placeholder="企業コード">
                </div>
                <div class="inline-radio divPadding">
                    <div><input type="radio" name="rootPath" @change="execChoice('CorporateId', 'rootPath')"><label>企業情報</label></div>
                    <div><input type="radio" name="rootPath" @change="execChoice('LoginUser', 'rootPath')"><label>管理者情報</label></div>
                    <div><input type="radio" name="rootPath" @change="execChoice('root', 'rootPath')"><label>ルート</label></div>
                </div>
                <div class="inputPath divPadding" v-show="reactiveObj.rootPath == 'CorporateId' && reactiveObj.rootPath">
                    <p class="cautionMessage">※固定の文字列のパス以外は「*」表記にすること</p>
                    <div class="inputExecPath">
                        <input id="displayRootPath" v-model="reactiveObj.displayRootPath" disabled><input type="text" id="inputPath" placeholder="後続のパス">
                    </div>
                </div>
                <div class="divPadding">
                    <button id="commonButton" @click="showDefaultModal(`入力した内容で設定します。<br>よろしいですか？`)">設定する</button>
                </div>
            </div>
            <DefaultModalComponent
                :key="reactiveModalObj.modalRenderKey"
                :modalMessage="reactiveModalObj.modalMessage"
                v-show="reactiveModalObj.modalFlag"
                @executeMethod="closeDefaultModal">
            </DefaultModalComponent>
        </div>
    </main>
</template>

<style scoped>
    .database {
        padding: 0 5% 0 25%;
        background-color: #e7e6e6;
    }
    .cation {
        color: red;
        font-size: 20px;
        font-weight: bold;
    }
    .divPadding {
        padding: 0 0 30px 0;
    }
    .inline-radio {
        display: flex;
        border-radius: 3px;
        overflow: hidden;
        border: none;
        div {
            position: relative;
            flex: 1;
        }
        input {
            width: 100%;
            height: 60px;
            opacity: 0;
        }
        label {
            position: absolute;
            top: 0; left: 0;
            width: 100%;
            height: 100%;
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
            border-right: 1px solid #b6b6b6;
        }
        div:last-child label {
            border-right: 0;
        }
        input:checked + label {
            background: #00AEEB;
            font-weight: 500;
            color: #fff;
        }
    }
    .jsonData {
        .inputJson {
            display: flex;
            justify-content: center;
            #fileLabel {
                display: flex;
                position: relative;
                border-radius: 10px;
                justify-content: center;
                align-items: center;
                border: solid 2px #999999;
                background-color: #EEEEEF;
                width: 50%;
                height: 60%;
                line-height: 300%;
            }
            #fileButton {
                display: none;
            }
        }
        .jsonDataContent {
            p {
                padding: 2% 0 2% 0;
            }
            #jsonData {
                border-radius: 10px;
                border: solid 2px #999999;
                background-color: #EEEEEF;
                width: 100%;
                height: 500px;
            }
        }
    }
    .inputPath {
        .cautionMessage {
            color: red;
            text-align: left;
        }
        .inputExecPath {
            display: flex;
            #displayRootPath {
                height: 60px;
                width: 20%;
                border-radius: 10px;
                border: solid 2px #999999;
                background-color: #EEEEEF;
            }
            #inputPath {
                height: 60px;
                width: 80%;
                border-radius: 10px;
                border: solid 2px #999999;
                background-color: #EEEEEF;
            }
        }
    }
    .inputCorporate {
        #inputCorporate {
            height: 60px;
            width: 100%;
            border-radius: 10px;
            border: solid 2px #999999;
            background-color: #EEEEEF;
        }
    }
    #commonButton {
        margin: 15px 0 0 0;
    }
    @media (max-width: 1150px) {
        .database{
            padding: 0 5% 0 5%;
        }
        .editArea {
            overflow-x: scroll;
        }
    }
</style>
